import { SystemConfiguration } from 'app/interfaces/Config';
import { LoginUser, OperatorLogged } from 'app/interfaces/User';
import { FilterSpareParts, FilterWorkOrders } from 'app/types/filters';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

export interface PersistedDateRange {
  startDate: string | null;
  endDate: string | null;
}

export type PersistedListFilters = Record<string, (string | number)[]>;

interface SessionStore {
  loginUser: LoginUser | undefined;
  operatorLogged: OperatorLogged | undefined;
  autoSignedOperatorCode: string | undefined;
  filterWorkOrders: FilterWorkOrders | undefined;
  filterSpareParts: FilterSpareParts | undefined;
  listDateFilters: Record<string, PersistedDateRange>;
  listStatusFilters: Record<string, PersistedListFilters>;
  isMenuOpen: boolean;
  config: SystemConfiguration | undefined;
  _hasHydrated: boolean;
}

interface SessionActions {
  setLoginUser: (loginUser: LoginUser | undefined) => void;
  setOperatorLogged: (operatorLogged: OperatorLogged | undefined) => void;
  setAutoSignedOperatorCode: (code: string | undefined) => void;
  setFilterWorkOrders: (filterWorkOrders: FilterWorkOrders | undefined) => void;
  setFilterSpareParts: (filterSpareParts: FilterSpareParts | undefined) => void;
  setListDateFilters: (key: string, dateRange: PersistedDateRange) => void;
  setListStatusFilters: (key: string, filters: PersistedListFilters) => void;
  setIsMenuOpen: (isMenuOpen: boolean) => void;
  setConfig: (config: SystemConfiguration) => void;
  setHasHydrated: (state: boolean) => void;
}

const STORE_VERSION = 1;

const getInitialSessionState = (): SessionStore => ({
  loginUser: undefined,
  operatorLogged: undefined,
  autoSignedOperatorCode: undefined,
  filterWorkOrders: undefined,
  filterSpareParts: undefined,
  listDateFilters: {},
  listStatusFilters: {},
  isMenuOpen: false,
  config: undefined,
  _hasHydrated: false,
});

export const useSessionStore = create(
  persist<SessionStore & SessionActions>(
    set => ({
      ...getInitialSessionState(),
      setLoginUser: value => {
        set(
          value === undefined
            ? { loginUser: value, autoSignedOperatorCode: undefined }
            : { loginUser: value }
        );
      },
      setOperatorLogged: value => {
        set({ operatorLogged: value });
      },
      setAutoSignedOperatorCode: value => {
        set({ autoSignedOperatorCode: value });
      },
      setFilterWorkOrders: value => {
        set({ filterWorkOrders: value });
      },
      setFilterSpareParts: value => {
        set({ filterSpareParts: value });
      },
      setListDateFilters: (key, dateRange) => {
        set(state => ({
          listDateFilters: { ...state.listDateFilters, [key]: dateRange },
        }));
      },
      setListStatusFilters: (key, filters) => {
        set(state => ({
          listStatusFilters: { ...state.listStatusFilters, [key]: filters },
        }));
      },
      setIsMenuOpen: value => {
        set({ isMenuOpen: value });
      },
      setConfig: value => {
        set({ config: value });
      },
      setHasHydrated: value => {
        set({ _hasHydrated: value });
      },
    }),
    {
      name: 'session-storage',
      version: STORE_VERSION,
      storage: createJSONStorage(() => sessionStorage),
      onRehydrateStorage: () => state => {
        state?.setHasHydrated(true);
      },
      migrate: persistedState => {
        // No borrar la sesión al cambiar de build: rescatar el estado válido
        // de cualquier versión previa (incluidas las versiones-timestamp legacy)
        const state = persistedState as Partial<SessionStore>;
        if (!state || typeof state !== 'object') {
          return getInitialSessionState() as SessionStore & SessionActions;
        }
        return {
          ...getInitialSessionState(),
          loginUser: state.loginUser,
          operatorLogged: state.operatorLogged,
          autoSignedOperatorCode: state.autoSignedOperatorCode,
          filterWorkOrders: state.filterWorkOrders,
          filterSpareParts: state.filterSpareParts,
          listDateFilters: state.listDateFilters ?? {},
          listStatusFilters: state.listStatusFilters ?? {},
          isMenuOpen: state.isMenuOpen ?? false,
          config: state.config,
        } as SessionStore & SessionActions;
      },
    }
  )
);

interface GlobalStore {
  isModalOpen: boolean;
  showModalBackground: boolean;
  isMainScrollEnabled: boolean;
}

interface GlobalActions {
  setIsModalOpen: (value: boolean) => void;
  setShowModalBackground: (value: boolean) => void;
  setIsMainScrollEnabled: (value: boolean) => void;
}

export const useGlobalStore = create<GlobalStore & GlobalActions>(set => ({
  isModalOpen: false,
  showModalBackground: false,
  isMainScrollEnabled: true,
  setIsModalOpen: (value: boolean) => {
    set({ isModalOpen: value });
  },
  setShowModalBackground: (value: boolean) => {
    set({ showModalBackground: value });
  },
  setIsMainScrollEnabled: (value: boolean) => {
    set({ isMainScrollEnabled: value });
  },
}));
